import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { AdminSidebar } from './AdminSidebar';
import { AdminTopNav } from './AdminTopNav';
import { AdminMobileSidebar } from './AdminMobileSidebar';
import { useContextHydration } from '@app/hooks/useContextHydration';
import { BillingAlertsBanner } from '@features/billing/components/BillingAlertsBanner';
import { useContextStore } from '@app/store/context.store';
import { useAuthStore } from '@app/store/auth.store';
import { canActAsTenant, shouldShowTenantSwitcher } from '@features/auth/roles';
import { routeNeedsTenant } from '../lib/route-scope';
import { requestContext } from '@core/api/requestContext';

const BILLING_PAGES = new Set([
  '/admin/billing',
  '/admin/platform-billing',
]);

/**
 * Blocks tenant-scoped pages until an active tenant is resolved, so no page
 * mounts and fires a headerless (tenant-less) API call on login/refresh — the
 * cause of the error burst. Platform/account routes need no tenant and render
 * immediately.
 */
function TenantGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const activeTenantId = useContextStore((s) => s.activeTenantId);
  const user = useAuthStore((s) => s.user);
  const needsTenant = routeNeedsTenant(pathname);

  // Keep the axios header source (requestContext singleton) in sync with the
  // store DURING RENDER — before any child page mounts and fires a request.
  // On refresh the store rehydrates the tenant id before requestContext does,
  // so without this a tenant page could send a request with no x-tenant-id.
  if (activeTenantId && requestContext.getTenantId() !== activeTenantId) {
    requestContext.setTenantId(activeTenantId);
  }

  // Defensive auto-select: a single-membership tenant user with no active tenant
  // (edge case) shouldn't dead-end — pick their only tenant.
  useEffect(() => {
    if (activeTenantId || !user || canActAsTenant(user)) return;
    if (user.memberships?.length === 1) {
      useContextStore.getState().setActiveTenantId(user.memberships[0].tenantId);
    }
  }, [activeTenantId, user]);

  if (!needsTenant || activeTenantId) return <>{children}</>;

  const canSelect = shouldShowTenantSwitcher(user) || canActAsTenant(user);
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="max-w-sm rounded-xl border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <p className="text-sm font-semibold">Select a tenant to continue</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {canSelect
            ? 'Use the tenant switcher in the top-right to choose a tenant, then this view will load.'
            : 'No tenant is available for your account yet. Please contact an administrator.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Root shell for the admin panel.
 *
 * Desktop  → persistent collapsible sidebar (left)
 * Mobile   → sidebar hidden; opens as Sheet via hamburger
 *
 * Note: ChatBubble preview is intentionally removed. The widget is for
 * client sites only — embed it via the "Get Embed Code" button on each agent.
 */
export default function AdminLayout() {
  useContextHydration();
  const { pathname } = useLocation();
  const activeTenantId = useContextStore((s) => s.activeTenantId);
  // Billing alerts are tenant-scoped — only show (and fetch) with an active tenant.
  const showLayoutAlerts = !BILLING_PAGES.has(pathname) && !!activeTenantId;

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ backgroundColor: 'var(--background)' }}
    >
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <AdminSidebar />
      </div>

      {/* Mobile sidebar */}
      <div className="md:hidden">
        <AdminMobileSidebar />
      </div>

      {/* Right: topnav + scrollable content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <AdminTopNav />

        <main className="flex-1 overflow-y-auto">
          {/* Subtle dot-grid background texture */}
          <div
            className="min-h-full"
            style={{
              backgroundImage:
                'radial-gradient(circle, var(--border) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          >
            <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
              <TenantGate>
                {showLayoutAlerts && <BillingAlertsBanner className="mb-4" />}
                <Outlet />
              </TenantGate>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
